// core/auth.js — autenticação via Google OAuth (ID token) + sessão persistente.
//
// Substitui a versão legada por CPF (preservada em core/auth.legacy.js).
// A API pública foi mantida estável para não quebrar os 9 módulos consumidores:
//
//   Funções preservadas:
//     auth.setAlunos(lista)     — injeta lista de alunos da API (lookup email → aluno)
//     auth.getAlunos()          — cópia da lista atual
//     auth.getCurrent()         — usuário da sessão ({nome, turma, cpf, role, email, picture}) ou null
//     auth.isAdmin()            — true se role === 'admin'
//     auth.isLoggedIn()         — true se há sessão válida (<24h)
//     auth.logout()             — limpa sessão (sessionStorage + GIS autoSelect)
//     auth.onChange(fn)         — listener de mudança de sessão; retorna unsubscribe
//     auth.primeiroNome(user?)  — primeiro nome para saudações
//
//   Funções novas:
//     auth.signInWithGoogleToken(idToken) — autentica a partir do ID token do GIS
//     auth.restoreSession()              — restaura sessão do sessionStorage no boot
//     auth.isSecretariaEmailDivergente() — sinaliza fallback "e-mail não cadastrado"
//
//   Funções removidas (NUNCA voltam — regra explícita do plano):
//     auth.tryLogin(mode, cpf) — CPF deixa de ser login
//     ADMIN_HARDCODED          — admin master agora vem da whitelist do backend
//
// Decisões travadas:
//   - Whitelist de admins é resolvida pelo BACKEND (Apps Script / Script Properties).
//     O frontend recebe `role` via endpoint /sessao. Nunca confia apenas no email.
//   - Lookup aluno é por email Google (case-insensitive, trim). Dedup por CPF na
//     FICHA LIMPA é responsabilidade de quem chama setAlunos (core/api.js).
//   - Se email não casar com nenhum aluno nem whitelist, signIn retorna erro
//     'EMAIL_NAO_CADASTRADO' → UI mostra fallback WhatsApp para secretaria.

import { decodeIdToken, disableAutoSelect } from './google-auth.js';
import { saveSession, loadSession, clearSession, patchSession } from './session.js';

// —————————————— Estado em memória ——————————————

let alunos = [];                 // Lista de alunos vinda da API (sem admin injetado)
let currentUser = null;          // Usuário ativo desta aba
let emailDivergente = null;      // Guarda email do Google quando não casar, p/ UI fallback
const listeners = new Set();

function notify() {
  for (const fn of listeners) {
    try { fn(currentUser); } catch (e) { console.error('[auth] listener erro:', e); }
  }
}

// —————————————— Helpers privados ——————————————

function normEmail(e) {
  return typeof e === 'string' ? e.trim().toLowerCase() : '';
}

function findAlunoByEmail(email) {
  const needle = normEmail(email);
  if (!needle) return null;
  return alunos.find(a => normEmail(a.email) === needle) || null;
}

// —————————————— Lista de alunos ——————————————

/**
 * Substitui a lista de alunos (fonte: FICHA LIMPA via core/api.js).
 * Aceita objetos com shape { nome, turma, cpf, email, ... }.
 */
function setAlunos(lista) {
  if (!Array.isArray(lista)) {
    console.error('[auth] setAlunos: lista inválida', lista);
    return;
  }
  alunos = lista.filter(a => a && (a.email || a.cpf));
}

function getAlunos() {
  return alunos.slice();
}

// —————————————— Sign-in ——————————————

/**
 * Autentica a partir do ID token do Google.
 *
 * Fluxo local (frontend-only, pré-backend):
 *   1. Decodifica o token → email, name, picture
 *   2. Procura o email na lista de alunos (carregada via setAlunos)
 *   3. Se encontrou, cria sessão como 'aluno'
 *   4. Se não encontrou, NÃO cria sessão — lança erro EMAIL_NAO_CADASTRADO
 *      (role 'admin' será resolvido quando o backend responder — commit 6)
 *
 * Quando o backend /sessao estiver pronto (commit 6), esta função vai chamar
 * o endpoint que valida o token e retorna {role, cpf, nome, turma}. Por ora,
 * seguimos com lookup local + marcação temporária 'aluno'.
 *
 * @param {string} idToken
 * @returns {Promise<{ok:true, user:object} | {ok:false, code:string, email?:string, nome?:string}>}
 */
async function signInWithGoogleToken(idToken) {
  const payload = decodeIdToken(idToken);
  if (!payload) return { ok: false, code: 'TOKEN_INVALIDO' };
  if (!payload.email_verified) return { ok: false, code: 'EMAIL_NAO_VERIFICADO' };

  const email = normEmail(payload.email);
  const nomeGoogle = payload.name || '';
  const picture = payload.picture || '';

  const aluno = findAlunoByEmail(email);
  if (!aluno) {
    emailDivergente = { email, nome: nomeGoogle };
    return { ok: false, code: 'EMAIL_NAO_CADASTRADO', email, nome: nomeGoogle };
  }

  const user = {
    idToken,
    email,
    nome: aluno.nome || nomeGoogle,
    picture,
    role: 'aluno',
    cpf: aluno.cpf || '',
    turma: aluno.turma || '',
  };
  currentUser = user;
  emailDivergente = null;
  saveSession(user);
  notify();
  return { ok: true, user };
}

/**
 * Promove o usuário atual a admin (usado quando o backend confirmar whitelist).
 * Hoje (pré-commit 6), pode ser chamado pelo login screen se o email estiver
 * em uma lista branca temporária no frontend.
 *
 * @param {{role?: 'admin'|'aluno', cpf?:string, turma?:string, nome?:string}} patch
 */
function patchCurrent(patch) {
  if (!currentUser) return null;
  currentUser = { ...currentUser, ...patch };
  patchSession(patch);
  notify();
  return currentUser;
}

/**
 * Restaura sessão do sessionStorage no boot. Idempotente.
 * Retorna o usuário restaurado ou null.
 */
function restoreSession() {
  const s = loadSession();
  if (!s) return null;
  currentUser = {
    idToken: s.idToken,
    email: s.email,
    nome: s.nome,
    picture: s.picture,
    role: s.role,
    cpf: s.cpf,
    turma: s.turma,
  };
  notify();
  return currentUser;
}

// —————————————— Consultas ——————————————

function getCurrent() {
  return currentUser;
}

function isAdmin() {
  return currentUser?.role === 'admin';
}

function isLoggedIn() {
  return currentUser !== null;
}

function isSecretariaEmailDivergente() {
  return emailDivergente;
}

function clearEmailDivergente() {
  emailDivergente = null;
}

// —————————————— Logout ——————————————

async function logout() {
  if (currentUser === null) return;
  currentUser = null;
  emailDivergente = null;
  clearSession();
  await disableAutoSelect();
  notify();
}

// —————————————— Listeners ——————————————

function onChange(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// —————————————— Saudações ——————————————

function primeiroNome(user = currentUser) {
  if (!user?.nome) return '';
  return user.nome.split(' ')[0];
}

// —————————————— Export ——————————————

export const auth = {
  setAlunos,
  getAlunos,
  signInWithGoogleToken,
  patchCurrent,
  restoreSession,
  getCurrent,
  isAdmin,
  isLoggedIn,
  isSecretariaEmailDivergente,
  clearEmailDivergente,
  logout,
  onChange,
  primeiroNome,
};
